/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import braintree from 'braintree-web';
import mbpLogger from 'mbp-logger';
import { withRouter } from 'react-router';
import {
    bool, func, string, object, number, shape, arrayOf,
} from 'prop-types';
import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import { withStyles } from '@material-ui/core/styles';

import GooglePay from './GooglePay/GooglePay';

import {
    getGooglePayClientId,
    submitGooglePayment,
} from '../../../../../../../state/ducks/Payment/plugins/GooglePay/GooglePay-Actions';

import {
    getPaymentClient,
    getGoogleTransactionInfo,
    googlePayPluginConfig,
    getOrderItemId,
    getShippingOption,
    getMonthStartDate,
    getOrderTotalSummaryInfo,
    getDateDeliveryFormat,
    getGoogleUnserviceableAddressError,
} from '../../../../../../../state/ducks/Payment/plugins/GooglePay/GooglePay-Helper';

import { setRecipientAddress } from '../../../../../../../state/ducks/Payment/plugins/GooglePay/GooglePay-Operations';
import { googlePayClientId } from '../../../../../../../state/ducks/Payment/plugins/GooglePay/GooglePay-Selectors';

import {
    getOrderTotalSummary, getTotalItemInCart,
} from '../../../../../../../state/ducks/Common/Common-Selectors';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlags } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getIsPassportUserLoggedIn } from '../../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { loadOrderId, loadCartCount } from '../../../../../../../state/ducks/Order/Order-Actions';
import { loadCart, cartLoaded } from '../../../../../../../state/ducks/Cart/Cart-Actions';
import orderClient from '../../../../../../../apis/checkout-apis/orderClient';
import { withAuth } from '../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import cartServices from '../../../../../../../apis/cart-service-apis/cartServices';

const styles = (theme) => ({
    loaderText: {
        fontStyle: 'italic',
        fontWeight: '800',
        textAlign: 'center',
        color: theme.palette.colorNeutral70,
    },
    containerStyle: {
        padding: '10px 0px',
        textAlign: 'center',
    },
});

class GooglePayButton extends React.Component {
    constructor(props) {
        super(props);
        this.googlePaymentInstance = null;
        this.googlePaymentClient = null;
        this.initGooglePayButton = false;
        this.GooglePayButtonLoaded = false;
        this.orderId = '';
        this.orderItemId = '';
        this.addItemInProgress = false;
    }

    componentDidMount() {
        const {
            getClientId,
            clientId,
            buttonPosition,
        } = this.props;

        if (buttonPosition === 'primary' && !clientId) {
            getClientId();
        }
    }

    getCart = async (orderId) => {
        const {
            callLoadCart, callCartLoaded, callLoadCartCount, auth,
        } = this.props;
        const jwtToken = await auth.getAccessTokenSafely();

        let response = {};
        try {
            const cartResponse = await cartServices.getCartOrderDetails({
                wcEnv: {},
                jwtToken,
                cartId: orderId,
            });

            if (cartResponse.data) {
                const cartCount = cartResponse.data.orderSize;
                callLoadCartCount(cartCount);
                callLoadCart(cartResponse.data);
                callCartLoaded();
                response = cartResponse.data;
                return response;
            }
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'getCart',
                message: 'Error Getting Cart Data in Google Pay',
                orderId: this.orderId,
            });

            response = {
                transactionState: 'ERROR',
                error: {
                    intent: 'PAYMENT_AUTHORIZATION',
                    message: 'Error Calculating Shipping in the order',
                    reason: 'GET_CART_INVALID',
                },
            };
        }
        return response;
    }

    addItemToCart = async () => {
        const {
            auth, selectedSkuId, selectedSkuBrand, callLoadOrderId,
        } = this.props;

        const jwtToken = await auth.getAccessTokenSafely();

        let returnData = {};

        // proceed to add item to cart
        try {
            const addToCartResponse = await orderClient.addToCart({}, jwtToken, null, {
                brandCode: selectedSkuBrand,
                productCode: selectedSkuId,
                quantity: 1,
            }, '');

            const arrOrderId = addToCartResponse?.data?.orderId;
            if (arrOrderId) {
                // log order Id to store
                callLoadOrderId(arrOrderId);

                const orderId = arrOrderId[0];
                this.orderId = orderId;

                // Get Cart
                const cartResponse = await this.getCart(orderId);
                const orderItemId = getOrderItemId(cartResponse);

                this.orderItemId = orderItemId;

                returnData = {
                    orderId,
                    orderItemId,
                    itemAdded: true,
                };
            }
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'performAddToCart',
                message: 'Init perform Add To Cart - Google Pay',
                ex,
            });

            returnData = {
                orderId: '',
                orderItemId: '',
                itemAdded: false,
            };
        }

        return returnData;
    }

    handlePaymentAuthorized = (paymentDataResponse) => new Promise(((resolve) => {
        const { orderId, orderItemId } = this;
        const {
            submitPayment,
            callTrackEvent, history, isCheckout,
        } = this.props;

        this.googlePaymentInstance.parseResponse(paymentDataResponse, (error, result) => {
            if (error) {
                resolve({
                    transactionState: 'ERROR',
                    error: {
                        intent: 'PAYMENT_AUTHORIZATION',
                        message: 'Payment Failed',
                        reason: 'PAYMENT_DATA_INVALID',
                    },
                });

                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    module: 'mbp-pwa-ui',
                    function: 'handlePaymentAuthorized',
                    jsError: error,
                    message: 'Google Payment failed',
                });
            }
            if (result) {
                mbpLogger.logDebug({
                    appName: process.env.npm_package_name,
                    module: 'mbp-pwa-ui',
                    function: 'handlePaymentAuthorized',
                    message: 'payment authorized - Google Pay',
                });

                callTrackEvent({
                    eventCategory: isCheckout ? 'Checkout' : 'Product Page',
                    eventAction: 'GooglePay Authorized Success',
                    eventLabel: 'Payment',
                });

                submitPayment({
                    orderId,
                    orderItemId,
                    paymentDataResponse,
                    result,
                    history,
                    isCheckout,
                });

                resolve({ transactionState: 'SUCCESS' });
            }
        });
    }));

    getCalculationShipping = async () => {
        const { orderId } = this;
        const { auth, featureFlags } = this.props;
        const jwtToken = await auth.getAccessTokenSafely();

        let response = {};

        try {
            const headers = {};
            const siteExpirement = featureFlags['site-experiment-mode'] || {};

            if (siteExpirement.value) {
                headers.sv = siteExpirement.value;
            }

            const calcResponse = await orderClient.calculateShipping({}, jwtToken, orderId, false, headers);

            response = calcResponse?.data;
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'getCalculationShipping',
                message: 'Error Getting Calculation Shipping in Google Pay',
                orderId: this.orderId,
            });

            response = {
                transactionState: 'ERROR',
                error: {
                    intent: 'PAYMENT_AUTHORIZATION',
                    message: 'Error Calculating Shipping in the order',
                    reason: 'CALCULATION_SHIPPING_INVALID',
                },
            };
        }
        return response;
    }

    handleShippingDateSelection = async (intermediatePaymentData) => {
        // google pay will return user selected date
        try {
            mbpLogger.logDebug({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'handleAddressSelection',
                message: 'Init handleShippingDateSelection - Google Pay',
                intermediatePaymentData,
            });

            const { shippingOptions, shippingOptionData } = intermediatePaymentData;
            const { orderId, orderItemId } = this;
            const { auth } = this.props;

            const jwtToken = await auth.getAccessTokenSafely();

            if (orderItemId && shippingOptionData.id) {
                const delivery = getDateDeliveryFormat(shippingOptionData.id);

                // // save user selected delivery data
                await orderClient.updateCart(
                    {},
                    jwtToken,
                    orderItemId,
                    '',
                    delivery,
                    null,
                    orderId,
                );

                // calculate shipping
                await this.getCalculationShipping();

                // Get Cart
                const cartResponse = await this.getCart(orderId);

                // Get Order Summary Total
                const orderTotalSummary =  getOrderTotalSummaryInfo(cartResponse);

                // Get new transaction information
                const orderSummary = await getGoogleTransactionInfo(orderTotalSummary);

                return {
                    result: {
                        shippingOptions,
                        orderSummary,
                    },
                    status: 'success',
                };
            }
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'handleShippingDateSelection',
                message: 'Cannot delivery to the selected date - Google Pay',
                intermediatePaymentData,
                ex,
            });

            return {
                result: {
                    reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
                    message: 'Cannot delivery to the selected date',
                    intent: 'SHIPPING_ADDRESS',
                },
                status: 'error',
            };
        }
        return {};
    }

    handleAddressSelection = async (intermediatePaymentData) => {
        // google pay will return user selected recipient address
        try {
            mbpLogger.logDebug({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'handleAddressSelection',
                message: 'Init handleAddressSelection - Google Pay',
                intermediatePaymentData,
            });

            const { shippingAddress } = intermediatePaymentData;
            const startDate = getMonthStartDate();
            const { orderId, orderItemId } = this;
            const { auth, passportUser } = this.props;

            const jwtToken = await auth.getAccessTokenSafely();

            if (orderItemId) {
                // save recipient details
                await setRecipientAddress(jwtToken, orderId, orderItemId, shippingAddress);

                // Get Delivery Calendar Dates
                const calendarResponse = await orderClient.getDeliveryCalendar(
                    {},
                    jwtToken,
                    orderItemId,
                    startDate,
                    orderId,
                );

                const calendarData = calendarResponse?.data?.response;
                const shippingOptions = getShippingOption(calendarData, passportUser);

                // Save default date
                const delivery = getDateDeliveryFormat(shippingOptions.defaultSelectedOptionId);
                await orderClient.updateCart(
                    {},
                    jwtToken,
                    orderItemId,
                    '',
                    delivery,
                    null,
                    orderId,
                );

                // calculate shipping
                await this.getCalculationShipping();

                // Get Cart
                const cartResponse = await this.getCart(orderId);

                // Get Order Summary Total
                const orderTotalSummary =  getOrderTotalSummaryInfo(cartResponse);

                // Get new transaction information
                const orderSummary = await getGoogleTransactionInfo(orderTotalSummary);

                return {
                    result: {
                        shippingOptions,
                        orderSummary,
                    },
                    status: 'success',
                };
            }
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'handleAddressSelection',
                message: 'Cannot ship to the selected address - Google Pay',
                errorResult: ex,
                intermediatePaymentData,
            });

            return {
                result: {
                    reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
                    message: 'Cannot ship to the selected address',
                    intent: 'SHIPPING_ADDRESS',
                },
                status: 'error',
            };
        }
        return {};
    }

    handlePaymentDataChanged = async (intermediatePaymentData) => {
        const paymentDataRequestUpdate = {};
        try {
            mbpLogger.logDebug({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'onPaymentDataChanged',
                message: 'Init Payment Data Changed - Google Pay',
                intermediatePaymentData,
            });

            if (!this.orderId && !this.addItemInProgress && intermediatePaymentData.callbackTrigger === 'INITIALIZE') {
                this.addItemInProgress = true;
                const responseAddToCart = await this.addItemToCart();

                if (!responseAddToCart.itemAdded) {
                    paymentDataRequestUpdate.error = getGoogleUnserviceableAddressError();
                }
            }

            if ((this.orderId && intermediatePaymentData.callbackTrigger === 'INITIALIZE') || intermediatePaymentData.callbackTrigger === 'SHIPPING_ADDRESS') {
                // Proceed save recipient address
                // Save User Selected Recipient Address and Get Shipping Options, Order Summary
                const reponseData = await this.handleAddressSelection(intermediatePaymentData);

                if (reponseData.status === 'success') {
                    const { shippingOptions, orderSummary } = reponseData.result;
                    paymentDataRequestUpdate.newShippingOptionParameters = shippingOptions;
                    paymentDataRequestUpdate.newTransactionInfo = orderSummary;
                } else if (reponseData.status === 'error') {
                    const { result } = reponseData;
                    paymentDataRequestUpdate.error = result;
                }
            } else if (intermediatePaymentData.callbackTrigger === 'SHIPPING_OPTION') { // delivery date
                // proceed save delivery date
                // Save User Selected Shipping Option and Get Order Summary

                const reponseData = await this.handleShippingDateSelection(intermediatePaymentData);

                if (reponseData.status === 'success') {
                    const { orderSummary } = reponseData.result;
                    paymentDataRequestUpdate.newTransactionInfo = orderSummary;
                } else if (reponseData.status === 'error') {
                    const { result } = reponseData;
                    paymentDataRequestUpdate.error = result;
                }
            }
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'onPaymentDataChanged',
                message: 'Error Payment Data Changed - Google Pay',
                intermediatePaymentData,
                ex,
            });

            paymentDataRequestUpdate.error = getGoogleUnserviceableAddressError();
        }
        return paymentDataRequestUpdate;
    }

    getGooglePaymentsClient = () => {
        const { isCheckout } = this.props;
        const paymentClientConfig = {
            environment: googlePayPluginConfig.environment,
            paymentDataCallbacks: {
                onPaymentAuthorized: this.handlePaymentAuthorized,
            },
        };

        if (!isCheckout) {
            paymentClientConfig.paymentDataCallbacks.onPaymentDataChanged = this.handlePaymentDataChanged;
        }

        if (!this.googlePaymentClient && typeof window !== 'undefined') {
            this.googlePaymentClient = getPaymentClient(paymentClientConfig);
        }
        return this.googlePaymentClient;
    }

    removeItemFromCart = async () => {
        try {
            const { auth } = this.props;

            const jwtToken = await auth.getAccessTokenSafely();

            if (this.orderId) {
                await orderClient.removeOrderItem({}, jwtToken, this.orderItemId, this.orderId);

                this.getCart(this.orderId);

                this.orderId = '';
                this.orderItemId = '';
                this.addItemInProgress = false;
                this.initGooglePayButton = false;
            }
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'removeItemFromCart',
                jsError: ex,
                message: 'Failed - Canceled Payment Sheet - remove item from cart',
            });
        }
    }

    onGooglePaymentButtonClicked = async () => {
        const {
            isCheckout, orderSummary,
            callTrackEvent, countriesDeliverable,
        } = this.props;

        try {
            const transactionInfo = getGoogleTransactionInfo(orderSummary);
            const paymentDataRequest = this.googlePaymentInstance.createPaymentDataRequest({
                transactionInfo,
            });

            paymentDataRequest.merchantInfo = {
                merchantId: googlePayPluginConfig.merchantId,
                merchantName: googlePayPluginConfig.merchantName,
            };
            paymentDataRequest.emailRequired = true;
            paymentDataRequest.transactionInfo = transactionInfo;
            const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0];
            cardPaymentMethod.parameters.billingAddressRequired = true;
            cardPaymentMethod.parameters.billingAddressParameters = {
                format: 'FULL',
                phoneNumberRequired: true,
            };

            if (isCheckout) {
                paymentDataRequest.callbackIntents = ['PAYMENT_AUTHORIZATION'];
            } else {
                const countries = (countriesDeliverable?.length > 0) ? countriesDeliverable : ['US'];
                paymentDataRequest.callbackIntents = ['SHIPPING_ADDRESS',  'SHIPPING_OPTION', 'PAYMENT_AUTHORIZATION'];
                paymentDataRequest.shippingAddressRequired = true;
                paymentDataRequest.shippingAddressParameters = {
                    phoneNumberRequired: true,
                    allowedCountryCodes: countries,
                };
                paymentDataRequest.shippingOptionRequired = true;
            }

            callTrackEvent({
                eventCategory: isCheckout ? 'Checkout' : 'Product Page',
                eventAction: 'GooglePay button clicked and payment sheet initiated',
                eventLabel: 'Payment',
            });

            await this.googlePaymentClient.loadPaymentData(paymentDataRequest);
        } catch (ex) {
            if (!isCheckout && ex?.statusCode === 'CANCELED') {
                this.removeItemFromCart();
            }

            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'onGooglePaymentButtonClicked',
                jsError: ex,
                message: 'Failed to Init Google Payment Sheet',
            });
        }
    }

    addGooglePayButton = (buttonContainer) => {
        const { callTrackEvent, isCheckout } = this.props;
        if (typeof document !== 'undefined') {
            mbpLogger.logDebug({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'addGooglePayButton',
                message: `add GooglePay Button ${buttonContainer}}`,
            });

            callTrackEvent({
                eventCategory: isCheckout ? 'Checkout' : 'Product Page',
                eventAction: `GooglePay Button ${buttonContainer} Displayed`,
                eventLabel: 'Payment',
            });

            const paymentsClient = this.googlePaymentClient;
            const button = paymentsClient.createButton({
                onClick: this.onGooglePaymentButtonClicked,
                buttonType: 'long',
                buttonColor: 'black',
                buttonSizeMode: (isCheckout) ? 'static' : 'fill',
            });
            document.getElementById(buttonContainer).appendChild(button);
        }
    }

    initGooglePay = async (buttonContainer) => {
        this.initGooglePayButton = true;
        const { clientId } = this.props;
        const paymentsClient = this.getGooglePaymentsClient();

        mbpLogger.logDebug({
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'initGooglePay',
            message: 'Initialize Google Pay',
        });

        try {
            const clientInstance = await braintree.client.create({
                authorization: clientId,
            });

            this.googlePaymentInstance = await braintree.googlePayment.create({
                client: clientInstance,
                googlePayVersion: 2,
                googleMerchantId: googlePayPluginConfig.merchantId,
            });

            const response = await paymentsClient.isReadyToPay({
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: this.googlePaymentInstance.createPaymentDataRequest().allowedPaymentMethods,
                existingPaymentMethodRequired: true,
            });

            if (response.result) {
                this.addGooglePayButton(buttonContainer);
            }
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                function: 'initGooglePay',
                jsError: ex,
                message: 'Failed to Init Google Button',
            });
        }
    }

    displayLoadingText = (classes) => (
        <div className={classes.containerStyle}>
            <div className={classes.loaderText}>Loading GooglePay...</div>
        </div>
    );

    displayGooglePayContainer = (googlePayProps) => {
        const {
            isScriptLoaded,
            isGooglePaySupported,
        } = googlePayProps;

        const {
            clientId, buttonPosition, classes,
        } = this.props;
        const buttonContainer = `google-button-container-${buttonPosition}`;

        if (isGooglePaySupported
            && isScriptLoaded
            && clientId
            && !this.initGooglePayButton) {
            this.initGooglePay(buttonContainer);
        }

        return (
            <div className={classes.containerStyle} id={buttonContainer}>
                { !this.initGooglePayButton && this.displayLoadingText(classes) }
            </div>
        );
    }

    render() {
        const {
            isCheckout, featureFlags, noOfItemsInCart,
        } = this.props;

        let googlePayEnabled = false;

        if (isCheckout) {
            googlePayEnabled = featureFlags['is-google-pay-enabled'];
        } else {
            googlePayEnabled = featureFlags['is-pdp-google-pay-enabled'];
        }
        googlePayEnabled = true;
        if ((!this.orderId && noOfItemsInCart === 0 && !isCheckout) || isCheckout) {
            return (
                <GooglePay featureEnabled={googlePayEnabled} render={(googlePayProps) => this.displayGooglePayContainer(googlePayProps)} />
            );
        }

        return null;
    }
}

GooglePayButton.propTypes = {
    history: object.isRequired,
    buttonPosition: string.isRequired,
    clientId: string.isRequired,
    getClientId: func.isRequired,
    submitPayment: func.isRequired,
    isCheckout: bool,
    orderSummary: shape({
        orderTotalBalance: string.isRequired,
        totalTax: string.isRequired,
        orderTotalBeforeTaxes: string.isRequired,
        totalProduct: string.isRequired,
        totalShipping: number,
    }).isRequired,
    classes: object.isRequired,
    featureFlags: shape({
        'is-google-pay-enabled': bool,
        'is-pdp-google-pay-enabled': bool,
    }).isRequired,
    callTrackEvent: func.isRequired,
    callLoadOrderId: func.isRequired,
    selectedSkuId: string,
    selectedSkuBrand: string,
    callLoadCart: func.isRequired,
    callCartLoaded: func.isRequired,
    passportUser: bool.isRequired,
    noOfItemsInCart: number,
    countriesDeliverable: arrayOf(string),
    callLoadCartCount: func.isRequired,
    auth: shape({
        getAccessTokenSafely: func.isRequired,
    }).isRequired,
};

GooglePayButton.defaultProps = {
    isCheckout: true,
    noOfItemsInCart: 0,
    selectedSkuId: '',
    selectedSkuBrand: '',
    countriesDeliverable: ['US'],
};

const mapStateToProps = (state) => ({
    passportUser: getIsPassportUserLoggedIn(state),
    featureFlags: getFeatureFlags(state),
    clientId: googlePayClientId(state),
    orderSummary: getOrderTotalSummary(state),
    noOfItemsInCart: getTotalItemInCart(state),
});

const mapDispatchToProps = (dispatch) => ({
    getClientId: bindActionCreators(getGooglePayClientId, dispatch),
    submitPayment: bindActionCreators(submitGooglePayment, dispatch),
    callTrackEvent: bindActionCreators(trackEvent, dispatch),
    callLoadOrderId: bindActionCreators(loadOrderId, dispatch),
    callLoadCart: bindActionCreators(loadCart, dispatch),
    callCartLoaded: bindActionCreators(cartLoaded, dispatch),
    callLoadCartCount: bindActionCreators(loadCartCount, dispatch),
});

export default compose(
    withAuth,
    withRouter,
    withStyles(styles),
    connect(mapStateToProps, mapDispatchToProps),
)(GooglePayButton);
